import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Picker from 'emoji-picker-react';
import {
  QUICK_MESSAGE_REACTIONS,
  getMessageReactionGroups,
  normalizeMessageReaction,
} from '../../utils/messageReactions';

const messageReactionPickerStyle = {
  width: 'min(340px, calc(100vw - 32px))',
  height: 'min(360px, 62vh)',
  background: '#171a21',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  boxShadow: '0 18px 46px -18px rgba(0, 0, 0, 0.82)',
};

export function MessageReactionSummary({
  message,
  currentPlayerId,
  own = false,
}) {
  const reactionGroups = useMemo(
    () => getMessageReactionGroups(message, currentPlayerId),
    [currentPlayerId, message],
  );

  if (!reactionGroups.length) return null;

  return (
    <div
      className={own ? 'messageReactionSummary own' : 'messageReactionSummary'}
      aria-label="Message reactions"
    >
      {reactionGroups.map(({ reaction, count, isMine }) => (
        <span
          key={reaction}
          className={['messageReactionSummaryPill', isMine ? 'mine' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <span aria-hidden="true">{reaction}</span>
          {count > 1 ? <span>{count}</span> : null}
        </span>
      ))}
    </div>
  );
}

export default function MessageReactions({
  messageId,
  onReactMessage,
  isOpen = false,
  onOpenChange,
}) {
  const [showFullReactionPicker, setShowFullReactionPicker] = useState(false);
  const [reactionError, setReactionError] = useState('');
  const [isReacting, setIsReacting] = useState(false);
  const reactionRootRef = useRef(null);

  useEffect(() => {
    if (!isOpen && !showFullReactionPicker) return undefined;

    const closeTray = (event) => {
      if (reactionRootRef.current?.contains(event.target)) return;
      onOpenChange?.(false);
      setShowFullReactionPicker(false);
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      onOpenChange?.(false);
      setShowFullReactionPicker(false);
    };

    document.addEventListener('mousedown', closeTray);
    document.addEventListener('touchstart', closeTray);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', closeTray);
      document.removeEventListener('touchstart', closeTray);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onOpenChange, showFullReactionPicker]);

  useEffect(() => {
    onOpenChange?.(false);
    setShowFullReactionPicker(false);
    setReactionError('');
  }, [messageId, onOpenChange]);

  const submitReaction = useCallback(
    async (reactionValue) => {
      if (isReacting) return;
      const reaction = normalizeMessageReaction(reactionValue);
      if (!messageId || !reaction) return;

      setReactionError('');
      setIsReacting(true);

      try {
        await onReactMessage({ messageId, reaction });
        onOpenChange?.(false);
        setShowFullReactionPicker(false);
      } catch (err) {
        setReactionError('Reaction failed. Try again.');
      } finally {
        setIsReacting(false);
      }
    },
    [isReacting, messageId, onOpenChange, onReactMessage],
  );

  const handlePickedReaction = useCallback(
    (eventOrEmojiObject, maybeEmojiObject) => {
      submitReaction(maybeEmojiObject?.emoji || eventOrEmojiObject?.emoji);
    },
    [submitReaction],
  );

  return (
    <>
      <div
        className="messageReactionDock"
        ref={reactionRootRef}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="messageReactionTrigger"
          aria-label="React to message"
          aria-expanded={isOpen ? 'true' : 'false'}
          onClick={() => {
            onOpenChange?.(!isOpen);
            setShowFullReactionPicker(false);
          }}
        >
          <span aria-hidden="true">{'\u263A'}</span>
        </button>

        {isOpen ? (
          <div className="messageReactionTray" role="menu">
            {QUICK_MESSAGE_REACTIONS.map(({ reaction, label }) => (
              <button
                key={reaction}
                type="button"
                className="messageReactionOption"
                onClick={() => submitReaction(reaction)}
                disabled={isReacting}
                aria-label={`React with ${label}`}
              >
                {reaction}
              </button>
            ))}
            <button
              type="button"
              className="messageReactionOption messageReactionMore"
              onClick={() => setShowFullReactionPicker((value) => !value)}
              disabled={isReacting}
              aria-label="Choose another reaction"
            >
              +
            </button>
          </div>
        ) : null}

        {showFullReactionPicker ? (
          <div
            className="messageReactionEmojiPicker"
            aria-label="More message reactions"
          >
            <Picker
              onEmojiClick={handlePickedReaction}
              lazyLoadEmojis
              pickerStyle={messageReactionPickerStyle}
            />
          </div>
        ) : null}
      </div>

      {reactionError ? (
        <div
          className="messageReactionError"
          role="status"
        >
          {reactionError}
        </div>
      ) : null}
    </>
  );
}
